import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, fmtMoney, fmtDateTime, relTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Plus, FileText, Camera, Receipt, Pulse, ShieldCheck, DownloadSimple, LinkSimple, Cube, ChatCircle, Sparkle, Envelope, X as XIcon, Warning, Flag, Trash, Lightbulb } from "@phosphor-icons/react";
import AILanguageToneModal from "@/components/AILanguageToneModal";
import StageProgressBar from "@/components/StageProgressBar";
import McKinseyButton from "@/components/McKinseyButton";
import CaseUpdatesTab from "@/components/CaseUpdatesTab";
import CaseAttachments from "@/components/CaseAttachments";

const ALL_TABS = [
  { id: "overview", label: "Overview", Icon: FileText, minTier: 1 },
  { id: "activity", label: "Activity", Icon: Pulse, minTier: 1 },
  { id: "updates", label: "Updates", Icon: ChatCircle, minTier: 1 },
  { id: "evidence", label: "Evidence", Icon: Camera, minTier: 1 },
  { id: "documents", label: "Documents", Icon: FileText, minTier: 1 },
  { id: "milestones", label: "Milestones", Icon: Flag, minTier: 3 },
  { id: "comments", label: "Comments", Icon: ChatCircle, minTier: 1 },
  { id: "billing", label: "Billing", Icon: Receipt, minTier: 3 },
];

export default function CaseDetail() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 4;
  const TABS = ALL_TABS.filter(t => tier >= t.minTier);
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");
  const [activities, setActivities] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [aiDoc, setAiDoc] = useState(null);
  const [slaRisk, setSlaRisk] = useState(null);
  const [showBriefModal, setShowBriefModal] = useState(false);
  const [showCDModal, setShowCDModal] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [nextActions, setNextActions] = useState(null);
  const [naBusy, setNaBusy] = useState(false);
  const [rmmd, setRmmd] = useState(null);

  const runAIBrief = async ({ language, force }) => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-brief`, { language, force });
      setAiDoc({ kind: "brief", ...data });
      setShowBriefModal(false);
    } catch (e) {
      alert("AI Brief failed: " + (e.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };
  const runCD = async ({ language, tone, recipient_name, recipient_address }) => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/cease-desist`,
        { language, tone, recipient_name, recipient_address });
      setAiDoc({ kind: "letter", ...data });
      setShowCDModal(false);
    } catch (e) {
      alert("C&D letter failed: " + (e.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };
  const loadNextActions = async (force=false) => {
    setNaBusy(true);
    try {
      const { data } = force
        ? await api.post(`/cases/${caseId}/next-actions`, { force: true })
        : await api.get(`/cases/${caseId}/next-actions`);
      setNextActions(data);
    } catch (e) { /* ignore */ }
    finally { setNaBusy(false); }
  };
  useEffect(() => { loadNextActions(false); /* eslint-disable-next-line */ }, [caseId]);

  const load = async () => {
    const [c1, a1, e1, i1, ci, pi] = await Promise.all([
      api.get(`/cases/${caseId}`),
      api.get(`/cases/${caseId}/activities`),
      api.get(`/cases/${caseId}/evidence`),
      api.get(`/cases/${caseId}/investigations`),
      api.get(`/invoices/client`),
      api.get(`/invoices/contractor`),
    ]);
    setC(c1.data); setActivities(a1.data); setEvidence(e1.data); setInvestigations(i1.data);
    setClientInvoices(ci.data.filter(x => x.case_id === caseId));
    setContractorInvoices(pi.data.filter(x => x.case_id === caseId));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  useEffect(() => {
    api.get(`/cases/${caseId}/sla-risk`).then((r) => setSlaRisk(r.data)).catch(() => {});
    api.get(`/cases/${caseId}/rm-md`).then((r) => setRmmd(r.data)).catch(() => {});
  }, [caseId]);

  if (!c) return <div className="font-mono text-xs text-[var(--span-text-2)]">LOADING CASE…</div>;

  return (
    <div className="space-y-5" data-testid="case-detail">
      <div className="flex items-center justify-between">
        <Link to="/cases" className="text-[12px] text-[var(--span-text-2)] hover:text-black flex items-center gap-1.5 font-mono">
          <ArrowLeft size={12} /> All cases
        </Link>
        <div className="flex items-center gap-2">
          <button
            data-testid="ai-brief-btn"
            onClick={() => setShowBriefModal(true)}
            className="span-btn span-btn-ghost h-8 text-[11px]"
            title="AI Field Officer Brief (Claude Sonnet 4.5) — EN / HI / BN"
          >
            <Sparkle size={12} /> AI brief
          </button>
          <button
            data-testid="ai-cd-btn"
            onClick={() => setShowCDModal(true)}
            className="span-btn span-btn-ghost h-8 text-[11px]"
            title="Draft Cease & Desist letter — language EN/HI/BN/AR × tone firm/escalation/final"
          >
            <Envelope size={12} /> C&D letter
          </button>
          <button data-testid="court-bundle-btn" onClick={async () => {
            const { data } = await api.get(`/cases/${caseId}/court-bundle`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            const a = document.createElement("a"); a.href = url; a.download = `bundle_${c.case_code}.pdf`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} className="span-btn span-btn-ghost h-8 text-[11px]">
            <DownloadSimple size={12}/> Court bundle PDF
          </button>
          <button data-testid="court-bundle-vet-btn" onClick={async () => {
            const variant = window.prompt("Country variant? (standard / state-court / high-court / dgft / customs)", "standard");
            if (!variant) return;
            const counsel_kind = window.prompt("Counsel kind? (span_lawyer / external_counsel / associate)", "span_lawyer");
            const counsel_name = window.prompt("Counsel name (optional)") || "";
            const notes = window.prompt("Notes to counsel (optional)") || "";
            try {
              await api.post(`/cases/${caseId}/court-bundle/request-vetting`,
                { country_variant: variant, counsel_kind, counsel_name, notes });
              alert("Vetting requested. Legal team has been notified.");
            } catch (e) {
              alert("Vetting request failed: " + (e.response?.data?.detail || e.message));
            }
          }} className="span-btn span-btn-ghost h-8 text-[11px]" title="Send court bundle to SPAN Lawyer / External Counsel for vetting">
            <Envelope size={12}/> Request counsel vetting
          </button>
          <McKinseyButton sourceType="case" sourceId={caseId}
            path={`/cases/${caseId}/mckinsey/narrative`} label="McKinsey" />
        </div>
      </div>

      {showBriefModal && (
        <AILanguageToneModal mode="brief" submitting={aiBusy}
          onClose={() => setShowBriefModal(false)} onSubmit={runAIBrief}/>
      )}
      {showCDModal && (
        <AILanguageToneModal mode="cd" submitting={aiBusy}
          onClose={() => setShowCDModal(false)} onSubmit={runCD}/>
      )}

      {/* Stage progress bar for investigation-lifecycle cases */}
      {c && <StageProgressBar caseId={caseId} code={c.code} current={c.current_stage}
                                canEdit={true}
                                onChanged={(s) => setC(prev => ({ ...prev, current_stage: s }))}/>}

      {/* COO approval card — visible only if not yet approved */}
      {c && !c.coo_approved && (
        <div className="card-flat p-3 flex items-center justify-between" data-testid="coo-approve-card">
          <div>
            <div className="label-eyebrow">COO operational approval</div>
            <div className="text-[12px] text-[var(--span-text-2)]">Awaiting COO approval — required before enforcement.</div>
          </div>
          <button data-testid="coo-approve-btn" onClick={async () => {
            try { await api.put(`/cases/${caseId}/approve`);
              setC(prev => ({...prev, coo_approved: true})); }
            catch (e) { alert(e.response?.data?.detail || "Approval failed"); }
          }} className="span-btn">Approve matter</button>
        </div>
      )}

      {/* AI Next-Action suggestions (CEO Q · D3) */}
      <div className="card-flat p-4" data-testid="next-actions-card">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Lightbulb size={14} weight="duotone"/>
            <div className="font-display font-bold text-[13px]">What should I do next?</div>
          </div>
          <button data-testid="refresh-next-actions" onClick={() => loadNextActions(true)} disabled={naBusy}
                  className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">
            {naBusy ? "Thinking…" : (nextActions?.suggestions?.length ? "Regenerate" : "Suggest actions")}
          </button>
        </div>
        {nextActions?.suggestions?.length > 0 ? (
          <ol className="space-y-2">
            {nextActions.suggestions.map((s, i) => (
              <li key={i} className="hairline p-2.5" data-testid={`next-action-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="font-display font-bold text-[12px] text-[var(--span-accent)]">#{i+1}</span>
                  <div className="flex-1">
                    <div className="font-bold text-[12px]">{s.title}</div>
                    <div className="text-[11px] text-[var(--span-text-2)] mt-0.5">{s.description}</div>
                  </div>
                  <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 ${
                    s.urgency==="high"?"bg-red-50 text-red-700":
                    s.urgency==="medium"?"bg-amber-50 text-amber-700":
                    "bg-[var(--span-surface-3)]"}`}>{s.urgency}</span>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <div className="text-[11px] text-[var(--span-text-2)]">Click <b>Suggest actions</b> to get 3 AI-ranked recommendations (Claude Sonnet 4.5).</div>
        )}
      </div>
      {aiDoc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-6 overflow-auto" onClick={() => setAiDoc(null)} data-testid="ai-doc-modal">
          <div className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] max-w-[840px] w-full p-6 mt-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{aiDoc.kind === "brief" ? "AI FIELD OFFICER BRIEF" : "AI CEASE & DESIST LETTER"}</div>
                <div className="font-mono text-[10px] text-[var(--span-text-2)] mt-1">Engine: {aiDoc.engine} · {aiDoc.case_code}</div>
              </div>
              <button onClick={() => setAiDoc(null)} className="text-[var(--span-text-2)] hover:text-black"><XIcon size={18}/></button>
            </div>
            <pre className="mt-4 whitespace-pre-wrap text-[13px] leading-[1.55] font-sans max-h-[70vh] overflow-auto border border-[var(--span-border)] p-5 bg-[var(--span-bg)]">{aiDoc.content}</pre>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => navigator.clipboard.writeText(aiDoc.content)}
                className="span-btn span-btn-ghost h-8 text-[11px]"
              >
                Copy to clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SLA risk strip */}
      {slaRisk && slaRisk.risk_inr > 0 && (
        <div className={"flex items-center gap-3 p-3 px-4 border-l-4 " + (slaRisk.band === "critical" ? "border-red-600 bg-red-50" : slaRisk.band === "high" ? "border-amber-600 bg-amber-50" : "border-[var(--span-border-strong)] bg-[var(--span-bg)]")} data-testid="sla-risk-strip">
          <Warning size={16} className={slaRisk.band === "critical" ? "text-red-600" : "text-amber-600"} />
          <div className="text-[12px]">
            <span className="font-display font-bold">₹{slaRisk.risk_inr.toLocaleString("en-IN")} of exposure accumulated</span>
            <span className="text-[var(--span-text-2)]"> · {slaRisk.days_open} days open · {slaRisk.message}</span>
          </div>
        </div>
      )}

      <div className="card-flat p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="badge font-mono text-[12px]" data-testid="case-code-badge">{c.case_code}</span>
              {c.our_file_reference && (
                <span
                  className="font-mono text-[10px] tracking-wider px-2 py-1 bg-white/5 border border-[var(--span-border)] text-[var(--span-text-2)]"
                  data-testid="case-formal-ref"
                  title="Our official file reference — appears on every invoice, court filing, email and PDF"
                >
                  REF · {c.our_file_reference}
                </span>
              )}
              <span className={`badge ${c.status === "closed" ? "badge-success" : c.status === "in_progress" ? "badge-info" : c.status === "review" ? "badge-warn" : ""}`}>
                {c.status.replace("_", " ")}
              </span>
              <span className={`badge ${c.priority === "high" ? "badge-danger" : c.priority === "low" ? "badge-success" : "badge-warn"}`}>
                {c.priority} priority
              </span>
              {rmmd?.rm && (
                <span
                  className="font-mono text-[9px] tracking-[0.18em] px-2 py-1 bg-sky-500/10 text-sky-300 border border-sky-500/30 flex items-center gap-1"
                  data-testid="case-rm-badge"
                  title={`Relationship Manager · ${rmmd.rm.name}`}
                >
                  RM · {rmmd.rm.initials}
                </span>
              )}
              {rmmd?.md && (
                <span
                  className="font-mono text-[9px] tracking-[0.18em] px-2 py-1 bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1"
                  data-testid="case-md-badge"
                  title={`Mentor Director · ${rmmd.md.name}`}
                >
                  MD · {rmmd.md.initials}
                </span>
              )}
            </div>
            <h1 className="font-display text-[26px] font-bold tracking-tight">{c.title}</h1>
            <div className="text-[12px] text-[var(--span-text-2)] mt-1">
              Client: <span className="text-[var(--span-text)] font-medium">{c.client_name}</span> · Country: {c.country} · Created {fmtDate(c.created_at)}
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Est. value</div>
            <div className="font-display font-bold text-[26px] tnum">{fmtMoney(c.value_usd, "USD")}</div>
            {c.value_usd > 10000 && <div className="badge badge-danger mt-1">CEO escalated</div>}
          </div>
        </div>
        {c.description && <p className="text-[13px] text-[var(--span-text-2)] mt-4 leading-relaxed max-w-3xl">{c.description}</p>}
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--span-border)] flex gap-6" data-testid="case-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 py-3 text-[12px] font-medium border-b-2 transition-colors ${
              tab === t.id ? "border-black text-black" : "border-transparent text-[var(--span-text-2)] hover:text-black"
            }`}
          >
            <t.Icon size={14} /> {t.label.toUpperCase()}
            {t.id === "activity" && <span className="font-mono text-[10px] opacity-50">{activities.length}</span>}
            {t.id === "evidence" && <span className="font-mono text-[10px] opacity-50">{evidence.length}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card-flat lg:col-span-2 p-5">
            <div className="label-eyebrow mb-3">Investigations</div>
            {investigations.length === 0 && <div className="text-[12px] text-[var(--span-text-2)]">No investigations recorded.</div>}
            <ul className="space-y-3">
              {investigations.map((iv) => (
                <li key={iv.investigation_id} className="border-l-2 border-[var(--span-text)] pl-3">
                  <div className="text-[12px] font-medium">{iv.type} · {iv.location}</div>
                  <div className="text-[12px] text-[var(--span-text-2)]">{iv.findings}</div>
                  <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-1">{fmtDate(iv.created_at)} · {iv.status}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-flat p-5">
            <div className="label-eyebrow mb-3">Case Meta</div>
            <dl className="space-y-2 text-[12px]">
              {[
                ["Case ID", c.case_id],
                ["Type", c.code],
                ["Country", c.country],
                ["Sequence", c.sequence],
                ["Year", c.year],
                ["Created by", c.created_by],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-[var(--span-text-2)]">{k}</dt>
                  <dd className="font-mono">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {tab === "activity" && <ActivityTab caseId={caseId} activities={activities} onAdded={load} />}
      {tab === "updates" && <CaseUpdatesTab caseId={caseId} />}
      {tab === "evidence" && <EvidenceTab caseId={caseId} evidence={evidence} onAdded={load} />}
      {tab === "documents" && (
        <div className="card-flat p-5">
          <CaseAttachments caseId={caseId} />
        </div>
      )}
      {tab === "milestones" && <MilestonesTab caseId={caseId} />}
      {tab === "comments" && <CommentsTab caseId={caseId} />}
      {tab === "billing" && <BillingTab clientInvoices={clientInvoices} contractorInvoices={contractorInvoices} />}
    </div>
  );
}

function ActivityTab({ caseId, activities, onAdded }) {
  const [t, setT] = useState("note");
  const [d, setD] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!d) return;
    setBusy(true);
    try { await api.post(`/cases/${caseId}/activities`, { type: t, description: d }); setD(""); onAdded(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 flex gap-3 items-end" data-testid="add-activity-form">
        <div className="w-[140px]">
          <label className="label-eyebrow block mb-1.5">Type</label>
          <select value={t} onChange={(e) => setT(e.target.value)} className="span-input">
            <option value="note">Note</option><option value="investigation">Investigation</option>
            <option value="legal">Legal</option><option value="finance">Finance</option>
            <option value="field">Field</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="label-eyebrow block mb-1.5">Description</label>
          <input value={d} onChange={(e) => setD(e.target.value)} placeholder="What happened?" className="span-input" data-testid="activity-input" />
        </div>
        <button disabled={busy} className="span-btn" data-testid="add-activity-btn"><Plus size={12} /> Add</button>
      </form>

      <div className="card-flat">
        <ul className="divide-y divide-[var(--span-border)]">
          {activities.length === 0 && <li className="p-5 text-[12px] text-[var(--span-text-2)]">No activity yet.</li>}
          {activities.map((a) => (
            <li key={a.activity_id} className="px-5 py-3 flex items-start gap-3">
              <span className="badge text-[10px] mt-0.5">{a.type}</span>
              <div className="flex-1">
                <div className="text-[13px]">{a.description}</div>
                <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-1">
                  {a.user_name || "system"} · {fmtDateTime(a.timestamp)} · {relTime(a.timestamp)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function EvidenceTab({ caseId, evidence, onAdded }) {
  const [type, setType] = useState("photo");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    if (!desc) return;
    setBusy(true);
    try {
      let file_url = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_url = data.url;
      }
      await api.post(`/cases/${caseId}/evidence`, { type, description: desc, captured_by: "Field officer", file_url });
      setDesc(""); setFile(null); onAdded();
    } finally { setBusy(false); }
  };

  const anchor = async (eid) => {
    await api.post(`/evidence/${eid}/anchor`);
    onAdded();
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 flex items-end gap-3 flex-wrap" data-testid="add-evidence-form">
        <div className="w-[120px]">
          <label className="label-eyebrow block mb-1.5">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="span-input">
            <option>photo</option><option>video</option><option>document</option><option>sample</option><option>report</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="label-eyebrow block mb-1.5">Description</label>
          <input value={desc} onChange={(e) => setDesc(e.target.value)} className="span-input" data-testid="evidence-input" placeholder="On-site photograph of…"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">Attach file</label>
          <input type="file" data-testid="evidence-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-[11px]" accept=".png,.jpg,.jpeg,.webp,.pdf,.mp4,.mov" />
        </div>
        <button disabled={busy} className="span-btn" data-testid="add-evidence-btn"><Plus size={12} /> Add</button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {evidence.length === 0 && <div className="text-[12px] text-[var(--span-text-2)]">No evidence captured.</div>}
        {evidence.map((e) => (
          <div key={e.evidence_id} className="card-flat p-4" data-testid={`evidence-${e.evidence_id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="badge">{e.type}</span>
              <span className={`badge ${e.status === "verified" ? "badge-success" : "badge-warn"}`}>{e.status}</span>
            </div>
            <div className="text-[13px] font-medium">{e.description}</div>
            <div className="text-[10px] font-mono text-[var(--span-text-2)] mt-1">
              {e.captured_by || "?"} · {fmtDate(e.captured_at)} · sig:{e.signature_hash?.slice(0, 10)}
            </div>
            {e.file_url && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${e.file_url}`} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-[var(--span-accent)] hover:underline mt-2 inline-flex items-center gap-1">
                <LinkSimple size={11}/> Open attachment
              </a>
            )}
            <div className="mt-3 border-t border-[var(--span-border)] pt-2">
              <div className="label-eyebrow mb-1.5 flex items-center gap-1"><ShieldCheck size={11}/> Chain of custody</div>
              <ol className="space-y-1.5">
                {e.chain?.map((co) => (
                  <li key={co.custody_id} className="text-[11px] flex items-start gap-2">
                    <span className="font-mono text-[var(--span-text-2)] w-[90px] shrink-0">{fmtDateTime(co.timestamp)}</span>
                    <span className="font-mono badge text-[10px] uppercase">{co.action}</span>
                    <span className="text-[var(--span-text-2)]">{co.handler}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="mt-3">
              <button data-testid={`anchor-${e.evidence_id}`} onClick={() => anchor(e.evidence_id)} className="span-btn span-btn-ghost h-7 text-[11px]">
                <Cube size={11}/> Anchor on blockchain
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingTab({ clientInvoices, contractorInvoices }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card-flat">
        <div className="px-5 py-3 border-b border-[var(--span-border)]">
          <div className="label-eyebrow">Client invoices</div>
          <div className="font-display font-semibold text-[15px]">Receivables</div>
        </div>
        <table className="span-table">
          <thead>
            <tr><th>#</th><th>Currency</th><th className="text-right">Total</th><th>Status</th></tr>
          </thead>
          <tbody>
            {clientInvoices.length === 0 && <tr><td colSpan={4} className="text-center py-5 text-[12px] text-[var(--span-text-2)]">No client invoices.</td></tr>}
            {clientInvoices.map(i => (
              <tr key={i.invoice_id}>
                <td className="font-mono">{i.invoice_number}</td>
                <td className="font-mono">{i.currency}</td>
                <td className="text-right tnum font-mono">{fmtMoney(i.total, i.currency)}</td>
                <td><span className={`badge ${i.status === "paid" ? "badge-success" : "badge-info"}`}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-flat">
        <div className="px-5 py-3 border-b border-[var(--span-border)]">
          <div className="label-eyebrow">Supplier invoices</div>
          <div className="font-display font-semibold text-[15px]">Payables</div>
        </div>
        <table className="span-table">
          <thead>
            <tr><th>Supplier</th><th>Currency</th><th className="text-right">Amount</th><th>Status</th></tr>
          </thead>
          <tbody>
            {contractorInvoices.length === 0 && <tr><td colSpan={4} className="text-center py-5 text-[12px] text-[var(--span-text-2)]">No contractor invoices.</td></tr>}
            {contractorInvoices.map(i => (
              <tr key={i.invoice_id}>
                <td className="text-[12px]">{i.contractor_name}</td>
                <td className="font-mono">{i.currency}</td>
                <td className="text-right tnum font-mono">{fmtMoney(i.amount, i.currency)}</td>
                <td><span className={`badge ${i.status === "paid" ? "badge-success" : i.status === "hold" ? "badge-warn" : "badge-info"}`}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CommentsTab({ caseId }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/cases/${caseId}/comments`).then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data));
  }, [caseId]);

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/comments`, { body, mentions });
      setBody(""); setMentions([]); load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="comments-tab">
      <form onSubmit={add} className="card-flat p-4">
        <textarea data-testid="comment-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a comment… use @ to mention" className="span-input min-h-[80px]" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {users.slice(0, 8).map(u => (
            <button type="button" key={u.user_id} onClick={() => setMentions(m => m.includes(u.email) ? m.filter(x => x !== u.email) : [...m, u.email])}
                    className={`badge cursor-pointer ${mentions.includes(u.email) ? "badge-info" : ""}`}>
              @{u.email.split("@")[0]}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] font-mono text-[var(--span-text-2)]">{mentions.length} mentioned · they will receive a notification</span>
          <button disabled={busy} className="span-btn h-8 text-[11px]" data-testid="post-comment">{busy ? "Posting…" : "Post comment"}</button>
        </div>
      </form>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-[12px] text-[var(--span-text-2)]">No comments yet — start the thread.</div>}
        {items.map(c => (
          <div key={c.comment_id} className="card-flat p-4" data-testid="comment-item">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[var(--span-surface-2)] flex items-center justify-center font-mono text-[10px] font-semibold">
                  {(c.user_name || c.user_email || "?").slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div className="text-[12px] font-medium">{c.user_name || c.user_email}</div>
                  <div className="text-[10px] font-mono text-[var(--span-text-2)]">{fmtDateTime(c.created_at)}</div>
                </div>
              </div>
            </div>
            <div className="text-[13px] mt-2 whitespace-pre-wrap">{c.body}</div>
            {c.mentions?.length > 0 && (
              <div className="flex gap-1 mt-2">
                {c.mentions.map(m => <span key={m} className="badge badge-info">@{m.split("@")[0]}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}



function MilestonesTab({ caseId }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/cases/${caseId}/milestones`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [caseId]);

  const add = async (e) => {
    e.preventDefault();
    if (!title) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/milestones`, { title, due_date: due || null, status: "pending" });
      setTitle(""); setDue(""); load();
    } finally { setBusy(false); }
  };

  const setStatus = async (m, status) => {
    await api.patch(`/cases/${caseId}/milestones/${m.milestone_id}`, { status });
    load();
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this milestone?")) return;
    await api.delete(`/cases/${caseId}/milestones/${m.milestone_id}`);
    load();
  };

  const STATUS = [
    { id: "pending",      label: "Pending",     cls: "" },
    { id: "in_progress",  label: "In progress", cls: "badge-info" },
    { id: "done",         label: "Done",        cls: "badge-success" },
    { id: "skipped",      label: "Skipped",     cls: "" },
  ];

  return (
    <div className="space-y-4" data-testid="milestones-tab">
      <form onSubmit={add} className="card-flat p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="label-eyebrow block mb-1.5">Milestone</label>
          <input data-testid="milestone-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. File FIR with local police" className="span-input"/>
        </div>
        <div className="w-[180px]">
          <label className="label-eyebrow block mb-1.5">Due date</label>
          <input type="date" data-testid="milestone-due" value={due} onChange={(e) => setDue(e.target.value)} className="span-input"/>
        </div>
        <button disabled={busy || !title} data-testid="add-milestone-btn" className="span-btn"><Plus size={12}/> Add</button>
      </form>

      <div className="card-flat">
        {items.length === 0 && <div className="p-5 text-[12px] text-[var(--span-text-2)]">No milestones yet. Add steps to track investigation progress.</div>}
        <ul className="divide-y divide-[var(--span-border)]">
          {items.map((m, idx) => (
            <li key={m.milestone_id} className="px-5 py-3 flex items-center gap-3" data-testid={`milestone-row-${idx}`}>
              <div className="w-7 h-7 flex items-center justify-center bg-[var(--span-surface-2)] font-mono text-[11px] font-semibold">{idx + 1}</div>
              <div className="flex-1">
                <div className="text-[13px] font-medium">{m.title}</div>
                <div className="text-[10px] font-mono text-[var(--span-text-2)] mt-0.5">
                  {m.due_date ? `Due ${fmtDate(m.due_date)}` : "No due date"} · added {relTime(m.created_at)}
                </div>
              </div>
              <select value={m.status} onChange={(e) => setStatus(m, e.target.value)} className={`badge ${STATUS.find((s) => s.id === m.status)?.cls || ""}`} data-testid={`milestone-status-${idx}`}>
                {STATUS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button onClick={() => remove(m)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]" data-testid={`delete-milestone-${idx}`}>
                <Trash size={14}/>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
